import React, { useEffect, useMemo, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import classnames from 'classnames'
import { Button, CloudinaryImage } from 'ethos-design-system'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import trustPilotFiveStar from '@/components/SingleCTA/icons/trust-pilot-5-star.svg'
import trustPilotSingleStar from '@/components/SingleCTA/icons/trust-pilot-single-star.svg'
import Markdown from '@/components/global/Markdown'

import { useHomePageConstants } from '@/hooks/content/useHomePageConstants'
import { useBrandColorsTreatment } from '@/hooks/features/useBrandColorsRefresh'
import { useDualCTAv3Treatment } from '@/hooks/features/useDualCTAv3Treatment'
import { usePricingStats } from '@/hooks/features/usePricingStats'
import { useTrustageLandingPage } from '@/hooks/features/useTrustageLandingPage'
import { useWreathsFooterUpdate } from '@/hooks/features/useWreathsFooterUpdate'
import useDetectScreenSize from '@/hooks/useDetectScreenSize'

import PriorityImage from '../../global/PriorityImage'
import styles from '../Trustworthy.module.scss'
import DualCTASection from '../common/DualCTASection'
import ArrowForward from '../icons/ArrowForward'
import { SingleCTAInterface } from '../interface'

const IconV2 = dynamic(
  () =>
    import('@getethos/ethos-design-system-v2').then((module) => module.Icon),
  { ssr: false }
)

interface TrustImage {
  alt: string
  imageUrl: string
  title: string
}

const arrowForward = (
  <div className={styles.arrowForward}>
    <ArrowForward />
  </div>
)

const VerticalAutoCarousel = ({
  height,
  children,
}: {
  height: number
  children: React.ReactNode
}) => {
  return (
    <Swiper
      direction="vertical"
      slidesPerView={1}
      loop={true}
      autoplay={{
        delay: 4000,
        disableOnInteraction: false,
      }}
      allowTouchMove={false} // Disable manual swiping
      modules={[Autoplay]}
      style={{ height: `${height}px` }}
      className={styles.verticalCarousel}
    >
      {children}
    </Swiper>
  )
}

const trustPilotDualCta = (useNewFonts: boolean) => (
  <div className={styles.trustPilotDualCta}>
    <img
      src={trustPilotSingleStar.src}
      className={styles.singleStar}
      alt="Trustpilot"
    />
    <img
      src={trustPilotFiveStar.src}
      className={styles.fiveStar}
      alt="Trustpilot"
    />
    <p className={classnames({ [styles.newFonts]: useNewFonts })}>
      <b>4.8</b> stars with <b>2500+</b> reviews on Trustpilot
    </p>
  </div>
)

const Trustworthy = ({
  moduleData,
  ctaClick,
  policiesApproved,
  secondCtaClick,
}: {
  moduleData: SingleCTAInterface
  ctaClick: () => void
  policiesApproved?: number
  secondCtaClick?: () => void
}) => {
  const {
    arrowIcon,
    ctaLabel,
    fiveMinutesHeading,
    heading,
    listType,
    useNewFonts = false,
  } = moduleData

  const { asPath } = useRouter()

  const isHomePage =
    asPath.split('?')[0] === '/' || asPath === '/demo/NewFonts/'

  const { isTreatment: isWreathsFooterUpdate } = useWreathsFooterUpdate()
  const [trustImages, setTrustImages] = useState<TrustImage[]>(
    features.SINGLE_CTA.trustImages
  )

  const {
    isTreatment: isPricingStatsTreatment,
    isTreatment1: isPricingStatsTreatment1,
    isTreatment2: isPricingStatsTreatment2,
  } = usePricingStats()

  const { isTabletAndSmaller, isMobile, width } = useDetectScreenSize()

  const { policiesApproved: homePagePoliciesApproved } = useHomePageConstants()

  const { isTreatment: isTrustageTreatment } = useTrustageLandingPage()

  const { isTreatment: isDualCTAv3Treatment } = useDualCTAv3Treatment()

  const { isTreatment: isBrandColorsTreatment } = useBrandColorsTreatment()

  useEffect(() => {
    if (!isHomePage) {
      return
    }

    if (
      useNewFonts &&
      isMobile &&
      trustImages !== features.SINGLE_CTA.newFontsDualCtaTrustImages
    ) {
      setTrustImages(features.SINGLE_CTA.newFontsDualCtaTrustImages)
      return
    } else if (
      useNewFonts &&
      trustImages !== features.SINGLE_CTA.newFontsDualCtaTrustImagesDesktop &&
      !isMobile
    ) {
      setTrustImages(features.SINGLE_CTA.newFontsDualCtaTrustImagesDesktop)
      return
    } else if (
      isMobile &&
      !useNewFonts &&
      trustImages !== features.SINGLE_CTA.dualCtaTrustImages
    ) {
      setTrustImages(features.SINGLE_CTA.dualCtaTrustImages)
      return
    } else if (
      !isMobile &&
      !useNewFonts &&
      trustImages !== features.SINGLE_CTA.dualCtaTrustImagesDesktop
    ) {
      setTrustImages(features.SINGLE_CTA.dualCtaTrustImagesDesktop)
      return
    } else if (
      [
        features.SINGLE_CTA.dualCtaTrustImagesDesktop,
        features.SINGLE_CTA.dualCtaTrustImages,
        features.SINGLE_CTA.newFontsDualCtaTrustImagesDesktop,
        features.SINGLE_CTA.newFontsDualCtaTrustImages,
      ].includes(trustImages)
    ) {
      return
    }

    if (isWreathsFooterUpdate) {
      setTrustImages(features.SINGLE_CTA.wreathsTrustImages)
      return
    }

    setTrustImages(features.SINGLE_CTA.trustImages)
  }, [isWreathsFooterUpdate, isMobile])

  let headingToShow = isTrustageTreatment
    ? 'Instant <br/> Life insurance'
    : heading

  if (isDualCTAv3Treatment) {
    headingToShow = 'Life insurance and<br/>wills in 10 minutes'
  }

  if (isPricingStatsTreatment1) {
    headingToShow = 'Save over 50%<br/>on life insurance'
  }

  if (isPricingStatsTreatment2) {
    headingToShow = 'Save over 45%<br/>on life insurance'
  }

  const policiesApprovedFormatted =
    homePagePoliciesApproved ||
    policiesApproved?.toLocaleString('en-US') ||
    '1,018'

  const defaultCardsInfo = [
    '**$2M** in coverage starts at **$2/day**',
    `**${policiesApprovedFormatted}** families approved today`,
    'No medical exams or blood tests',
  ]

  const treatmentCardsInfo = [
    '**$500k** in coverage starts at **$1/day**',
    `**${policiesApprovedFormatted}** families approved today`,
    'No medical exams or blood tests',
  ]

  const cardsInfo: string[] = isTrustageTreatment
    ? treatmentCardsInfo
    : defaultCardsInfo

  const bgImage = isTrustageTreatment
    ? 'https://res.cloudinary.com/getethos/image/upload/v1732263928/AdobeStock_410597901_web_cutout_1_qzmrz2.webp'
    : 'https://res.cloudinary.com/getethos/image/upload/v1736768846/a4a80674302dbb345546ec7ae210e8eb_trg43e_kc13kv.webp'

  const rotatingHeaderHeight = useMemo(() => {
    return isTabletAndSmaller ? 51 : 77
  }, [isTabletAndSmaller])

  const rotatingHeader = useMemo(() => {
    return (
      <div className={styles.headerContainer}>
        <VerticalAutoCarousel height={rotatingHeaderHeight}>
          {width > 330 ? (
            <SwiperSlide className={styles.carouselSlide}>
              <p>Get life insurance</p>
            </SwiperSlide>
          ) : null}

          <SwiperSlide className={styles.carouselSlide}>
            <p>Create a will</p>
          </SwiperSlide>
        </VerticalAutoCarousel>
        <p className={styles.bottomLine}>in 10 minutes</p>
      </div>
    )
  }, [rotatingHeaderHeight, width])

  const cardsInfoSection = useMemo(() => {
    return listType === 'checkmarks' ? (
      <div className="mb-6">
        {cardsInfo.map((card: string) => (
          <div key={card} className="mb-2 flex items-center gap-2 last:mb-0">
            <IconV2 name={'check'} sx={{ color: '#FFFFFF' }} />
            <p className="body-s-reg text-white">{card}</p>
          </div>
        ))}
      </div>
    ) : (
      <div className={styles.bentoContainer}>
        {cardsInfo.map((card: string) => (
          <div key={card} className={styles.box}>
            <div className={classnames(styles.boxContent)}>
              <Markdown input={card} />
            </div>
          </div>
        ))}
      </div>
    )
  }, [listType, cardsInfo])

  const headerContent = useMemo(
    () =>
      isHomePage && !isDualCTAv3Treatment && !isPricingStatsTreatment ? (
        rotatingHeader
      ) : (
        <Markdown
          className={classnames(styles.header, {
            [styles.singleCtaTreatment]: !isHomePage,
            [styles.newFonts]: useNewFonts,
          })}
          input={headingToShow || ''}
        ></Markdown>
      ),
    [
      isHomePage,
      isDualCTAv3Treatment,
      isPricingStatsTreatment,
      rotatingHeader,
      headingToShow,
      useNewFonts,
    ]
  )

  let ctaSection = (
    <div className={styles.cta}>
      <Button.Unstyled
        fullWidth
        arrowIcon={!!arrowIcon}
        onClick={() => ctaClick()}
      >
        {ctaLabel}
      </Button.Unstyled>
    </div>
  )

  const subheader = (
    <p className={styles.subheader}>{trustPilotDualCta(useNewFonts)}</p>
  )

  if (isHomePage) {
    ctaSection = (
      <DualCTASection
        arrowIcon={arrowIcon}
        ctaClick={ctaClick}
        secondCtaClick={secondCtaClick}
        arrowForward={arrowForward}
        useNewFonts={useNewFonts}
        brandColorsClassName={classnames({
          [styles.brandTreatment]: isBrandColorsTreatment,
        })}
      />
    )
  }

  return (
    <div className={classnames(styles.trustworthy)}>
      <div
        className={classnames(styles.container, {
          [styles.dualCtaTreatment]: isHomePage,
          [styles.brandTreatment]: isBrandColorsTreatment,
        })}
      >
        <div className={styles.mainContent}>
          <h1
            className={classnames(styles.header, {
              [styles.fiveMinutesHeading]: fiveMinutesHeading,
              [styles.newFonts]: useNewFonts,
              [styles.dualCtaTreatment]: isHomePage && isDualCTAv3Treatment,
            })}
          >
            {headerContent}
          </h1>
          {isHomePage ? subheader : cardsInfoSection}
          {ctaSection}

          <div
            className={classnames(styles.trustImages, {
              [styles.trustImagesWreaths]: isWreathsFooterUpdate,
              [styles.newFonts]: useNewFonts,
            })}
          >
            {trustImages.map((trustImage, index) => (
              <div
                key={`${trustImage.title}_${index}`}
                className={classnames(styles.trustImageWrapper, {
                  [styles.trustImageWrapperWreaths]: isWreathsFooterUpdate,
                })}
              >
                <div
                  key={`trustImage-${index}`}
                  className={classnames(styles.trustImageContainer, {
                    [styles.trustImageContainerWreaths]: isWreathsFooterUpdate,
                    [styles.dualCtaTreatment]: isHomePage,
                    [styles.newFonts]: useNewFonts,
                  })}
                >
                  <CloudinaryImage
                    publicId={trustImage.imageUrl}
                    alt={trustImage.alt}
                    className={styles.trustImage}
                    crop={CloudinaryImage.CROP_METHODS.FIT}
                    height={[55, 55, 55, 55]}
                    width={[90, 90, 90, 90]}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className={classnames(styles.bgImage, {
            [styles.tsBgImage]: isTrustageTreatment,
            [styles.dualCtaTreatment]: isHomePage,
            [styles.newFonts]: useNewFonts,
          })}
        >
          <PriorityImage
            publicId={bgImage}
            fetchpriority="high"
            alt="Father mother and child hugging"
            crop={CloudinaryImage.CROP_METHODS.FIT}
            height={[600, 768, 1440, 1920]}
            width={[300, 384, 729, 972]}
          />
        </div>
      </div>
    </div>
  )
}

const features = {
  SINGLE_CTA: {
    darkTrustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1742253551/00_CMS/business_insider_gray_ehoxtg.svg',
        alt: 'Business Insider',
        title: 'Best no medical exam<br />term life policy',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1742253382/00_CMS/cnbc_gray_mseozl.svg',
        alt: 'CNBC',
        title: 'Best burial insurance',
      },
    ],
    trustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1742495812/business_insider_light_qumbq8.svg',
        alt: 'Business Insider',
        title: `Best no medical exam <br/> term life policy`,
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1742253350/00_CMS/cnbc_white_x6tpeu.svg',
        alt: 'CNBC',
        title: 'Best burial insurance',
      },
    ],
    wreathsTrustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1743577034/no1_business_insider_white_apaul2.svg',
        alt: 'Business Insider',
        title: 'Best burial insurance',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1743577035/trusted_forbes_advisor_white_ulaiwn.svg',
        alt: 'Forbes Advisor',
        title: 'Best no medical exam<br />term life policy',
      },
    ],
    darkWreathsTrustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1743583862/no1_business_insider_dark_jirjkp.svg',
        alt: 'business insider',
        title: 'Best burial insurance',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1743583862/trusted_forbes_advisor_dark_kh8vrh.svg',
        alt: 'Forbes Advisor',
        title: 'Best no medical exam<br />term life policy',
      },
    ],
    willsTrustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1724067282/Frame_1000007064_yrt25p.svg',
        alt: '2 million families trust ethos',
        title: '2 million families trust ethos',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1724067282/Frame_1000007066_ua57ii.svg',
        alt: 'Best value for the money',
        title: 'Best value for the money',
      },
    ],
    dualCtaTrustImagesDesktop: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1747408112/no1_business_insider_updated.svg',
        alt: 'Business Insider',
        title: 'No-exam term life policy',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1747408107/num_1_estate_planning_updated.svg',
        alt: 'No. 1 Estate Planning',
        title: 'No. 1 Estate Planning',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1747408107/Forbes_Provider_updated2.svg',
        alt: 'Forbes',
        title: 'Trusted provider',
      },
    ],
    dualCtaTrustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1747408112/no1_business_insider_updated.svg',
        alt: 'Business Insider',
        title: 'No-exam term life policy',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1747408107/Forbes_Provider_updated2.svg',
        alt: 'Forbes',
        title: 'Trusted provider',
      },
    ],
    newFontsDualCtaTrustImagesDesktop: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1751280148/o5y5ztmmwxwivcqicopp.svg',
        alt: 'Business Insider',
        title: 'No-exam term life policy',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1751280148/bz2o0czknbzrwi09n7aq.svg',
        alt: 'No. 1 Estate Planning',
        title: 'No. 1 Estate Planning',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1751280148/gwfb2x9srbwu6esy8bzn.svg',
        alt: 'Forbes',
        title: 'Trusted provider',
      },
    ],
    newFontsDualCtaTrustImages: [
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1751280148/o5y5ztmmwxwivcqicopp.svg',
        alt: 'Business Insider',
        title: 'No-exam term life policy',
      },
      {
        imageUrl:
          'https://res.cloudinary.com/getethos/image/upload/v1751280148/gwfb2x9srbwu6esy8bzn.svg',
        alt: 'Forbes',
        title: 'Trusted provider',
      },
    ],
  },
}

export default Trustworthy
